import { useEffect, useMemo, useState } from 'react';
import { computeMetrics, difotTone, formatTimeAU, ZONE_COLOURS, type Row } from './lib';

/** Operations-floor large-screen display. Computes everything from the latest day. */
export function TVView({ data, onExit }: { data: Row[]; onExit: () => void }) {
  const [clock, setClock] = useState(() => new Date());

  // Toggle the body class that swaps the whole page into TV mode.
  useEffect(() => {
    document.body.classList.add('tv-mode');
    return () => document.body.classList.remove('tv-mode');
  }, []);

  // Clock + fullscreen + keyboard handling.
  useEffect(() => {
    const timer = setInterval(() => setClock(new Date()), 1000);
    try {
      void document.documentElement.requestFullscreen?.();
    } catch {
      /* ignore */
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onExit();
      if (e.key === 'f' || e.key === 'F') {
        if (document.fullscreenElement) void document.exitFullscreen();
        else void document.documentElement.requestFullscreen?.();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      clearInterval(timer);
      document.removeEventListener('keydown', onKey);
      try {
        if (document.fullscreenElement) void document.exitFullscreen();
      } catch {
        /* ignore */
      }
    };
  }, [onExit]);

  const view = useMemo(() => {
    const allDates = [...new Set(data.map((r) => r.d))].sort();
    const today = allDates[allDates.length - 1];
    const rows = data.filter((r) => r.d === today);
    const m = computeMetrics(rows);

    const byZone: Record<string, { total: number; complete: number; failed: number; denom: number; difot: number }> = {};
    rows.forEach((r) => {
      const z = r.r || 'Other';
      if (!byZone[z]) byZone[z] = { total: 0, complete: 0, failed: 0, denom: 0, difot: 0 };
      byZone[z].total++;
      if (r.st !== 'Cancelled') byZone[z].denom++;
      if (r.st === 'Complete') byZone[z].complete++;
      if (r.st === 'Failed') byZone[z].failed++;
      if (r.st === 'Complete' && r.ot === true) byZone[z].difot++;
    });
    const zoneOrder = Object.keys(byZone).sort((a, b) => byZone[b].total - byZone[a].total);

    const byDriver: Record<string, { name: string; total: number; complete: number; failed: number; difot: number; denom: number }> = {};
    rows.forEach((r) => {
      if (!r.dr) return;
      if (!byDriver[r.dr]) byDriver[r.dr] = { name: r.dr, total: 0, complete: 0, failed: 0, difot: 0, denom: 0 };
      const d = byDriver[r.dr];
      d.total++;
      if (r.st !== 'Cancelled') d.denom++;
      if (r.st === 'Complete') d.complete++;
      if (r.st === 'Failed') d.failed++;
      if (r.st === 'Complete' && r.ot === true) d.difot++;
    });
    const driverList = Object.values(byDriver)
      .filter((d) => d.total >= 5)
      .map((d) => ({ ...d, pct: d.denom ? (d.difot / d.denom) * 100 : 0 }))
      .sort((a, b) => b.pct - a.pct || b.total - a.total);

    const failures = rows.filter((r) => r.st === 'Failed').sort((a, b) => (b.ft || '').localeCompare(a.ft || ''));
    const bottomDrivers = driverList.slice().reverse().filter((d) => d.pct < 95 && d.failed > 0).slice(0, 5);

    const hours = Array.from({ length: 24 }, (_, i) => ({ h: i, complete: 0, failed: 0 }));
    rows.forEach((r) => {
      const t = r.ct || r.ft;
      if (!t) return;
      const sydHour = +new Date(t).toLocaleString('en-AU', { hour: '2-digit', hour12: false, timeZone: 'Australia/Melbourne' });
      if (r.st === 'Complete') hours[sydHour].complete++;
      if (r.st === 'Failed') hours[sydHour].failed++;
    });
    let firstHour = hours.findIndex((h) => h.complete + h.failed > 0);
    let lastHour = 23 - [...hours].reverse().findIndex((h) => h.complete + h.failed > 0);
    if (firstHour === -1) {
      firstHour = 6;
      lastHour = 18;
    }
    firstHour = Math.max(0, firstHour - 1);
    lastHour = Math.min(23, lastHour + 1);
    const windowed = hours.slice(firstHour, lastHour + 1);
    const peak = Math.max(1, ...windowed.map((h) => h.complete + h.failed));

    return { m, zoneOrder, byZone, driverList, failures, bottomDrivers, windowed, peak };
  }, [data]);

  const { m, zoneOrder, byZone, driverList, failures, bottomDrivers, windowed, peak } = view;
  const topN = driverList.slice(0, 7);

  return (
    <div id="tvView" aria-hidden="false">
      <button className="tv-exit" onClick={onExit} title="Exit TV mode (Esc)">
        EXIT
      </button>

      <div className="tv-head">
        <div className="tv-brand">
          <div className="logo">
            <svg width="60%" height="60%" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s-8-7.5-8-13a8 8 0 1 1 16 0c0 5.5-8 13-8 13z" />
              <circle cx="12" cy="9" r="3" />
            </svg>
          </div>
          <div>
            <div className="title">
              FERAMEX <span style={{ color: '#7FB5D9', fontWeight: 500, margin: '0 .3vw' }}>|</span>{' '}
              <span style={{ color: '#1A85C9' }}>DIFOT TODAY</span>
            </div>
            <div className="sub">Last-mile delivery operations · Powered by Locate2u</div>
          </div>
        </div>
        <div className="tv-time">
          <div className="clock">
            {clock.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'Australia/Melbourne' })}
          </div>
          <div className="date">
            {clock.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Australia/Melbourne' })}
          </div>
          <div className="pulse">
            <span className="live-dot" />
            Live · Refreshes every 60s
          </div>
        </div>
      </div>

      <div className="tv-kpis">
        <div className="tv-kpi hero">
          <div className="label">DIFOT</div>
          <div className="value">
            {m.difotPct.toFixed(1)}
            <span className="unit">%</span>
          </div>
          <div className="progress-bar">
            <div style={{ width: `${Math.min(100, m.difotPct)}%` }} />
          </div>
          <div className="sub">
            {m.difot} of {m.denom} stops delivered in full, on time
          </div>
        </div>
        <div className="tv-kpi">
          <div className="label">Delivered</div>
          <div className="value" style={{ color: '#0BDA8E' }}>
            {m.completed.toLocaleString()}
          </div>
          <div className="sub">{m.completionPct.toFixed(1)}% of scheduled</div>
        </div>
        <div className={`tv-kpi fail${m.failed > 0 ? ' alert-flash' : ''}`}>
          <div className="label">Failed</div>
          <div className="value">{m.failed}</div>
          <div className="sub">{m.failurePct.toFixed(1)}% failure rate</div>
        </div>
        <div className="tv-kpi info">
          <div className="label">In Progress</div>
          <div className="value">{m.pending}</div>
          <div className="sub">{m.drivers} drivers active</div>
        </div>
      </div>

      <div className="tv-middle">
        <div className="tv-section">
          <h2>
            By Zone <span className="badge">{zoneOrder.length}</span>
          </h2>
          <div>
            {zoneOrder.map((z) => {
              const v = byZone[z];
              const pct = v.denom ? (v.difot / v.denom) * 100 : 0;
              const tone = difotTone(pct);
              const colour = ZONE_COLOURS[z] || '#0061A2';
              return (
                <div className="state-row" key={z}>
                  <div className="code" style={{ color: colour, fontSize: '1vw', lineHeight: 1.1 }}>
                    {z}
                  </div>
                  <div className="bar-wrap">
                    <div className="meta">
                      <span>
                        {v.complete} delivered / {v.total} stops
                      </span>
                      {v.failed > 0 ? <span style={{ color: '#FF5245', fontWeight: 700 }}>{v.failed} failed</span> : <span>&nbsp;</span>}
                    </div>
                    <div className="bar">
                      <div style={{ width: `${pct}%`, background: tone }} />
                    </div>
                  </div>
                  <div className="pct" style={{ color: tone }}>
                    {pct.toFixed(1)}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="tv-section">
          <h2>Top Performers</h2>
          <div>
            {topN.map((d, i) => {
              const tone = difotTone(d.pct);
              return (
                <div className={`driver-row${i === 0 ? ' top1' : i === 1 ? ' top2' : i === 2 ? ' top3' : ''}`} key={d.name}>
                  <div className="rank">{i + 1}</div>
                  <div className="name">
                    {d.name}
                    <small>
                      {d.complete} delivered / {d.total} stops
                    </small>
                  </div>
                  <div className="score" style={{ color: tone }}>
                    {d.pct.toFixed(0)}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="tv-section">
          <h2>Needs Attention</h2>
          <div className="activity-feed">
            {failures.length === 0 && bottomDrivers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3vw 1vw', color: '#0BDA8E' }}>
                <div style={{ fontSize: '2.2vw', fontWeight: 800, marginBottom: '.4vw' }}>All clear</div>
                <div style={{ fontSize: '.95vw', color: '#8B92A6', fontWeight: 500 }}>No failures or underperforming drivers today</div>
              </div>
            ) : (
              <>
                {failures.slice(0, 6).map((f) => (
                  <div className="activity-item" key={`f-${f.id}`}>
                    <div className="accent" style={{ background: '#FF5245' }} />
                    <div className="body">
                      <div className="top">
                        <div className="so">
                          {f.so || f.ref}{' '}
                          <span style={{ color: '#FF5245', fontSize: '.7vw', fontWeight: 600, letterSpacing: '.1em', marginLeft: '.4vw' }}>FAILED</span>
                        </div>
                      </div>
                      <div className="driver">
                        {f.dr} · {f.r || ''}
                      </div>
                      <div className="note">{(f.fn || 'No reason recorded').slice(0, 70)}</div>
                    </div>
                    <div className="when">{f.ft ? formatTimeAU(f.ft).replace(' ', '') : '—'}</div>
                  </div>
                ))}
                {bottomDrivers.map((d) => (
                  <div className="activity-item" key={`d-${d.name}`}>
                    <div className="accent" style={{ background: '#FF9500' }} />
                    <div className="body">
                      <div className="top">
                        <div className="so">
                          {d.name}{' '}
                          <span style={{ color: '#FF9500', fontSize: '.7vw', fontWeight: 600, letterSpacing: '.1em', marginLeft: '.4vw' }}>UNDER 95%</span>
                        </div>
                      </div>
                      <div className="driver">
                        {d.failed} failed of {d.total} stops · {d.pct.toFixed(0)}% DIFOT
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="tv-bottom">
        <h2>
          <span>
            Delivery Volume by Hour <span style={{ color: '#0BDA8E', marginLeft: '.5vw' }}>— Today</span>
          </span>
          <span style={{ display: 'flex', gap: '1vw', fontSize: '.75vw', color: '#8B92A6' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.4vw' }}>
              <span style={{ width: '.6vw', height: '.6vw', background: '#0BDA8E', borderRadius: 2, display: 'inline-block' }} />
              Complete
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.4vw' }}>
              <span style={{ width: '.6vw', height: '.6vw', background: '#FF5245', borderRadius: 2, display: 'inline-block' }} />
              Failed
            </span>
          </span>
        </h2>
        <div className="hourly-chart">
          {windowed.map((h) => {
            const completePct = (h.complete / peak) * 100;
            const failedPct = (h.failed / peak) * 100;
            const total = h.complete + h.failed;
            return (
              <div className="hourly-bar" key={h.h} title={`${h.h}:00 — ${h.complete} complete, ${h.failed} failed`}>
                <div className="stack">
                  <div style={{ height: `${completePct}%`, background: '#0BDA8E' }} />
                  {h.failed ? <div style={{ height: `${failedPct}%`, background: '#FF5245' }} /> : null}
                </div>
                <div className="lab">
                  {String(h.h).padStart(2, '0')}
                  {total > 0 ? <span style={{ display: 'block', color: '#fff', fontSize: '.65vw', fontWeight: 700, marginTop: '.1vw' }}>{total}</span> : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="tv-press">
        Press <strong style={{ color: '#fff' }}>F</strong> for fullscreen · <strong style={{ color: '#fff' }}>ESC</strong> to exit
      </div>
    </div>
  );
}
